import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import TablePagination from '@mui/material/TablePagination';
import LuggageOutlinedIcon from '@mui/icons-material/LuggageOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bookingApi, bookingKeys } from '@/features/booking/api/booking.api';
import { guideFinanceKeys } from '@/features/guide-finance/api/guide-finance.api';
import { paymentApi } from '@/features/booking/api/payment.api';
import {
  completePaymentCheckout,
  verifyPaymentWithRetry,
} from '@/features/booking/utils/verifyPaymentAfterCheckout';
import { Button } from '@/shared/components/ui';
import { ProtectedRoute } from '@/shared/components/ProtectedRoute';
import { BookingStatus } from '@/types';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  needsContactConfirmation,
  TravelerContactDialog,
} from '@/features/booking/components/TravelerContactDialog';
import { userHasPaymentEmail } from '@/features/booking/utils/paymentEmail';
import { BookingReviewDialog } from '@/features/booking/components/BookingReviewDialog';
import { BookingDisputeDialog } from '@/features/booking/components/BookingDisputeDialog';
import { TravelerBookingCard } from '@/features/booking/components/TravelerBookingCard';
import { TravelerSosSheet } from '@/features/booking/components/TravelerSosSheet';
import { CancelBookingDialog } from '@/features/booking/components/CancelBookingDialog';
import { useTravelerBookings } from '@/features/booking/hooks/useTravelerBookings';
import type { Booking } from '@/types';

const HEADER_IMAGE = '/images/my%20booking.png';

const ACTIVE_STATUSES = [
  BookingStatus.PAYMENT_PENDING,
  BookingStatus.CONFIRMED,
  BookingStatus.UPCOMING,
  BookingStatus.IN_PROGRESS,
  BookingStatus.WAITING_FOR_TRAVELER_CONFIRMATION,
  BookingStatus.DISPUTED,
].join(',');

type BookingsTab = 'active' | 'past';
type PastTab = 'completed' | 'cancelled' | 'refunded';

const PAST_TAB_STATUS: Record<PastTab, string> = {
  completed: BookingStatus.COMPLETED,
  cancelled: [BookingStatus.CANCELLED, BookingStatus.EXPIRED].join(','),
  refunded: BookingStatus.REFUNDED,
};

const PAST_TAB_SORT: Record<PastTab, string> = {
  completed: 'completedAt',
  cancelled: 'cancelledAt',
  refunded: 'updatedAt',
};

const PAST_TAB_LABELS: Record<PastTab, string> = {
  completed: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

const TRUST_ITEMS = [
  {
    icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 22 }} />,
    title: 'Trusted local guides',
    description: 'Verified and experienced guides you can trust.',
  },
  {
    icon: <CreditCardOutlinedIcon sx={{ fontSize: 22 }} />,
    title: 'Secure & simple',
    description: 'Safe payments and easy booking process.',
  },
  {
    icon: <SupportAgentOutlinedIcon sx={{ fontSize: 22 }} />,
    title: "We're here to help",
    description: '24/7 support for all your travel needs.',
  },
] as const;

const tabSx = {
  minHeight: 52,
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
    bgcolor: 'primary.main',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9375rem',
    minHeight: 52,
    px: { xs: 2, sm: 3 },
    color: 'text.secondary',
    '&.Mui-selected': {
      color: 'text.primary',
      fontWeight: 700,
    },
  },
} as const;

function BookingsEmptyState({ variant }: { variant: 'active' | 'past' }) {
  const isActive = variant === 'active';

  return (
    <Box
      sx={{
        py: { xs: 6, sm: 8 },
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: '6px',
          mx: 'auto',
          mb: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'primary.50',
          color: 'primary.main',
        }}
      >
        {isActive ? (
          <LuggageOutlinedIcon sx={{ fontSize: 44 }} />
        ) : (
          <HistoryOutlinedIcon sx={{ fontSize: 44 }} />
        )}
      </Box>

      <Typography
        sx={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontWeight: 700,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          color: 'primary.dark',
          mb: 1,
        }}
      >
        {isActive ? 'No active bookings' : 'No past trips yet'}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ maxWidth: 400, mx: 'auto', mb: 3.5, lineHeight: 1.7 }}
      >
        {isActive
          ? 'When you book a local guide, your upcoming trips and payment status will appear here.'
          : 'Completed trips will show up here after you confirm trip completion with your guide.'}
      </Typography>

      {isActive && (
        <Button
          component={RouterLink}
          to="/guides"
          intent="primary-dark"
          startIcon={<ExploreOutlinedIcon />}
          sx={{ px: 3, py: 1.25, borderRadius: 1 }}
        >
          Find a guide
        </Button>
      )}
    </Box>
  );
}

function MyBookingsContent() {
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contactOpen, setContactOpen] = useState(() => needsContactConfirmation(user));
  const [paymentEmailOpen, setPaymentEmailOpen] = useState(false);
  const [pendingPaymentBookingId, setPendingPaymentBookingId] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [reviewBooking, setReviewBooking] = useState<Booking | null>(null);
  const [disputeBooking, setDisputeBooking] = useState<Booking | null>(null);
  const [sosBooking, setSosBooking] = useState<Booking | null>(null);
  const [cancelBookingId, setCancelBookingId] = useState<string | null>(null);
  const [pastPage, setPastPage] = useState(0);
  const [pastRowsPerPage, setPastRowsPerPage] = useState(10);

  const tab = (searchParams.get('tab') === 'past' ? 'past' : 'active') as BookingsTab;
  const pastTab = (
    searchParams.get('past') === 'cancelled' || searchParams.get('past') === 'refunded'
      ? searchParams.get('past')
      : 'completed'
  ) as PastTab;

  const setTab = (value: BookingsTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    if (value === 'active') next.delete('past');
    setSearchParams(next, { replace: true });
  };

  const setPastTab = (value: PastTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'past');
    next.set('past', value);
    setSearchParams(next, { replace: true });
    setPastPage(0);
  };

  const activeQuery = useTravelerBookings(
    { statuses: ACTIVE_STATUSES, limit: 50, sortBy: 'travelDate' },
    { enabled: tab === 'active' }
  );

  const pastQuery = useTravelerBookings(
    {
      statuses: PAST_TAB_STATUS[pastTab],
      sortBy: PAST_TAB_SORT[pastTab],
      page: pastPage + 1,
      limit: pastRowsPerPage,
    },
    { enabled: tab === 'past' }
  );

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: bookingKeys.all });

  useEffect(() => {
    const paymentId = paymentApi.getStoredPaymentId();
    if (!paymentId) return;

    let cancelled = false;
    void verifyPaymentWithRetry(paymentId)
      .then(() => {
        if (!cancelled) invalidate();
      })
      .catch(() => {
        // Ignore — user can retry payment from the booking card.
      });

    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  const retryPayment = useMutation({
    mutationFn: async (bookingId: string) => {
      const returnUrl = `${window.location.origin}/payment/return`;
      return paymentApi.initiate(bookingId, returnUrl);
    },
    onSuccess: async (payment) => {
      setPaymentError(null);
      try {
        await completePaymentCheckout(payment);
        invalidate();
      } catch (err) {
        setPaymentError((err as Error).message ?? 'Failed to confirm payment');
      }
    },
  });

  const startPayment = (bookingId: string) => {
    if (!userHasPaymentEmail(user)) {
      setPendingPaymentBookingId(bookingId);
      setPaymentEmailOpen(true);
      return;
    }
    retryPayment.mutate(bookingId);
  };

  const cancelBooking = useMutation({
    mutationFn: (bookingId: string) => bookingApi.cancel(bookingId),
    onSuccess: () => setCancelBookingId(null),
    onSettled: invalidate,
  });

  const confirmCompletion = useMutation({
    mutationFn: (bookingId: string) => bookingApi.confirmCompletion(bookingId),
    onSettled: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: guideFinanceKeys.all });
    },
  });

  const currentQuery = tab === 'active' ? activeQuery : pastQuery;
  const bookings = currentQuery.data?.data ?? [];
  const pastTotal = pastQuery.data?.pagination?.total ?? 0;

  return (
    <Box
      sx={{
        bgcolor: '#F1F5F9',
        minHeight: '100vh',
        py: { xs: 3, md: 5 },
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <TravelerContactDialog
          open={contactOpen}
          onClose={() => setContactOpen(false)}
          onConfirmed={() => setContactOpen(false)}
          description="Please confirm your name and phone so your guide can reach you."
        />

        <TravelerContactDialog
          open={paymentEmailOpen}
          onClose={() => {
            setPaymentEmailOpen(false);
            setPendingPaymentBookingId(null);
          }}
          requireEmail
          title="Email required for payment"
          description="Add your email to continue to secure checkout via Cashfree."
          onConfirmed={(updatedUser) => {
            updateUser(updatedUser);
            setPaymentEmailOpen(false);
            const bookingId = pendingPaymentBookingId;
            setPendingPaymentBookingId(null);
            if (bookingId) retryPayment.mutate(bookingId);
          }}
        />

        <BookingReviewDialog
          booking={reviewBooking}
          open={Boolean(reviewBooking)}
          onClose={() => setReviewBooking(null)}
          onSubmitted={invalidate}
        />

        <BookingDisputeDialog
          booking={disputeBooking}
          open={Boolean(disputeBooking)}
          onClose={() => setDisputeBooking(null)}
          onSubmitted={invalidate}
        />

        <TravelerSosSheet
          booking={sosBooking}
          open={Boolean(sosBooking)}
          onClose={() => setSosBooking(null)}
        />

        <CancelBookingDialog
          bookingId={cancelBookingId}
          open={Boolean(cancelBookingId)}
          cancelPending={cancelBooking.isPending}
          onClose={() => setCancelBookingId(null)}
          onConfirm={(id) => cancelBooking.mutate(id)}
        />

        {/* Header card */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 1.5,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 30px rgba(15, 23, 42, 0.06)',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
            minHeight: { md: 280 },
          }}
        >
          <Box
            sx={{
              p: { xs: 3, sm: 4, md: 4.5 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Box>
              <Typography
                component="h1"
                sx={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: 'primary.dark',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.15,
                  display: 'inline-block',
                  position: 'relative',
                  pb: 1,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: 42,
                    height: 3,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                  },
                }}
              >
                My bookings
              </Typography>
              <Typography
                sx={{
                  mt: 1.75,
                  color: 'text.secondary',
                  maxWidth: 420,
                  lineHeight: 1.7,
                  fontSize: { xs: '0.9375rem', md: '1rem' },
                }}
              >
                Manage upcoming trips, confirm completion, and review past bookings.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 1.75,
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 2px 10px rgba(15, 23, 42, 0.04)',
                maxWidth: 460,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CalendarMonthOutlinedIcon sx={{ fontSize: 22 }} />
              </Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  lineHeight: 1.6,
                  pt: 0.5,
                }}
              >
                Book local guides, explore new places, and create unforgettable memories.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 200, md: 'auto' },
              display: { xs: 'none', sm: 'block' },
            }}
          >
            <Box
              component="img"
              src={HEADER_IMAGE}
              alt="Travel destination"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        </Paper>

        {/* Bookings panel */}
        <Paper
          elevation={0}
          sx={{
            p: 0,
            overflow: 'hidden',
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 8px 30px rgba(15, 23, 42, 0.05)',
            mb: 3,
          }}
        >
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: 0.5 }}>
            <Tabs
              value={tab}
              onChange={(_, value: BookingsTab) => setTab(value)}
              sx={{
                ...tabSx,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Tab label="Active" value="active" />
              <Tab label="Past trips" value="past" />
            </Tabs>
          </Box>

          {tab === 'past' && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                px: { xs: 2, sm: 3 },
                py: 2,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: '#F8FAFC',
              }}
            >
              {(['completed', 'cancelled', 'refunded'] as PastTab[]).map((value) => (
                <Chip
                  key={value}
                  label={PAST_TAB_LABELS[value]}
                  onClick={() => setPastTab(value)}
                  color={pastTab === value ? 'primary' : 'default'}
                  variant={pastTab === value ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 600,
                    borderRadius: 2,
                    ...(pastTab !== value && { bgcolor: 'background.paper' }),
                  }}
                />
              ))}
            </Box>
          )}

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {currentQuery.isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress color="primary" />
              </Box>
            )}

            {currentQuery.isError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                Failed to load bookings. If you are signed in as a guide, use the guide dashboard for
                guide bookings.
              </Alert>
            )}

            {paymentError && (
              <Alert
                severity="error"
                sx={{ mb: 2.5, borderRadius: 2 }}
                onClose={() => setPaymentError(null)}
              >
                {paymentError}
              </Alert>
            )}

            {!currentQuery.isLoading && !currentQuery.isError && bookings.length === 0 && (
              <BookingsEmptyState variant={tab} />
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 2, md: 2.5 },
              }}
            >
              {bookings.map((booking) => (
                <TravelerBookingCard
                  key={booking.id}
                  booking={booking}
                  onPay={tab === 'active' ? startPayment : undefined}
                  onCancel={tab === 'active' ? setCancelBookingId : undefined}
                  onConfirmCompletion={
                    tab === 'active' ? (id) => confirmCompletion.mutate(id) : undefined
                  }
                  onDispute={tab === 'active' ? setDisputeBooking : undefined}
                  onSos={tab === 'active' ? setSosBooking : undefined}
                  onReview={setReviewBooking}
                  payPending={retryPayment.isPending}
                  cancelPending={cancelBooking.isPending}
                  confirmPending={confirmCompletion.isPending}
                />
              ))}
            </Box>

            {tab === 'past' && pastTotal > 0 && (
              <TablePagination
                component="div"
                count={pastTotal}
                page={pastPage}
                onPageChange={(_, nextPage) => setPastPage(nextPage)}
                rowsPerPage={pastRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setPastRowsPerPage(parseInt(event.target.value, 10));
                  setPastPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                sx={{
                  mt: 1,
                  borderTop: 1,
                  borderColor: 'divider',
                  '& .MuiTablePagination-toolbar': { px: 0 },
                }}
              />
            )}
          </Box>
        </Paper>

        {/* Trust strip */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(15, 23, 42, 0.04)',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            }}
          >
            {TRUST_ITEMS.map((item, index) => (
              <Box
                key={item.title}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.75,
                  p: { xs: 2.5, md: 3 },
                  borderRight: {
                    xs: 'none',
                    md: index < TRUST_ITEMS.length - 1 ? '1px solid' : 'none',
                  },
                  borderBottom: {
                    xs: index < TRUST_ITEMS.length - 1 ? '1px solid' : 'none',
                    md: 'none',
                  },
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '6px',
                    bgcolor: 'primary.50',
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.9375rem',
                      color: 'primary.dark',
                      mb: 0.5,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', lineHeight: 1.55 }}>
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export function MyBookingsPage() {
  return (
    <ProtectedRoute>
      <MyBookingsContent />
    </ProtectedRoute>
  );
}
